import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useRateLimit } from '@/hooks/useRateLimit'
import { useSecureStorage } from '@/hooks/useSecureStorage'
import { useLoginFlowHandlers } from '@/hooks/useLoginFlowHandlers'
import { useRecaptcha } from '@/hooks/useRecaptcha'
import { encodeLoginIntentTime } from '@/utils/security'
import type { Role } from '@/types'
import type { LoginStep } from '@/utils/loginSchemas'
import { getRoleFallbackPath } from '@/config/routeAccess'
import { LOCAL_AUTH_OIDC_STATUS, type AuthOidcStatus } from '@/services/authOidcService'
import api from '@/services/api'

/** SSO desativado — login sempre pelo formulário local. */
function readPreferLocalLogin(): boolean {
  return true
}

export function useLoginFlow() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const authReady = useAuthStore((s) => s.authReady)
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const sessionUsesCookie = useAuthStore((s) => s.sessionUsesCookie)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasValidSession = isAuthenticated && (Boolean(token) || sessionUsesCookie)
  const rateLimit = useRateLimit()
  const recaptcha = useRecaptcha()
  const [loginIntentTime, setLoginIntentTime] = useState(() => encodeLoginIntentTime())

  useEffect(() => {
    const refreshIntent = () => setLoginIntentTime(encodeLoginIntentTime())

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) refreshIntent()
    }

    let hiddenAt = 0
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      if (hiddenAt && Date.now() - hiddenAt > 60_000) refreshIntent()
    }

    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
  const { value: rememberedEmail, setValue: setRememberedEmail } = useSecureStorage('remember_email')

  const [email, setEmail] = useState(() => rememberedEmail || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => !!rememberedEmail)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [step, setStep] = useState<LoginStep>('credentials')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [oidcStatus] = useState<AuthOidcStatus>(LOCAL_AUTH_OIDC_STATUS)
  const [preferLocalLogin, setPreferLocalLogin] = useState(readPreferLocalLogin)
  const [oidcRedirecting, setOidcRedirecting] = useState(false)

  useEffect(() => {
    if (!authReady || !hasValidSession || !user) return
    navigate(getRoleFallbackPath(user.role, undefined, user), { replace: true })
  }, [authReady, hasValidSession, user, navigate, token, sessionUsesCookie])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlEmail = params.get('email')?.trim()
    const urlToken = params.get('token')?.trim()
    const urlStep = params.get('step')
    const oidc = params.get('oidc')

    if (urlEmail) setEmail(urlEmail)
    if (urlToken) setResetToken(urlToken)
    if (urlStep === 'forgotPassword') {
      setPreferLocalLogin(true)
      setStep('forgotPassword')
      setError('')
      setSuccessMessage('')
    } else if (urlStep === 'resetPassword' && urlToken) {
      setPreferLocalLogin(true)
      setStep('resetPassword')
      setSuccessMessage('')
      setError('')
    }

    if (oidc === 'error') {
      setPreferLocalLogin(true)
      setOidcRedirecting(false)
      setError(params.get('message') || 'Falha no login com Conecta ID. Use o formulário abaixo.')
      window.history.replaceState({}, '', '/login?local=1')
      return
    }

    if (oidc === 'success') {
      const accessToken = params.get('access_token')?.trim()
      setLoading(true)
      void (async () => {
        try {
          if (accessToken) {
            useAuthStore.getState().setToken(accessToken)
          }
          const me = await api.get('/auth/me')
          const meUser = me.data?.data?.user ?? me.data?.user
          if (!meUser) throw new Error('Sessão inválida')
          try {
            // Marca sessão OIDC para logout federado (encerra Keycloak no “Sair”).
            sessionStorage.setItem('crm_oidc_session', '1')
          } catch {
            /* ignore */
          }
          login(meUser, accessToken || '')
          window.history.replaceState({}, '', '/login')
          navigate(getRoleFallbackPath(meUser.role, undefined, meUser), { replace: true })
        } catch (err) {
          setPreferLocalLogin(true)
          setOidcRedirecting(false)
          setError('Login Conecta ID concluído, mas a sessão não foi estabelecida. Use o formulário abaixo.')
          window.history.replaceState({}, '', '/login?local=1')
        } finally {
          setLoading(false)
        }
      })()
    }
  }, [login, navigate])

  useEffect(() => {
    return () => {
      setPassword('')
      setTwoFactorCode('')
      setNewPassword('')
      setConfirmNewPassword('')
    }
  }, [])

  const redirectAfterAuth = (user: { role: Role; setor?: string | null }) => {
    navigate(getRoleFallbackPath(user.role, undefined, user), { replace: true })
  }

  const persistRememberMe = () => {
    if (rememberMe) {
      setRememberedEmail(email)
    } else {
      setRememberedEmail('')
    }
  }

  const {
    handleLoginSubmit,
    handleTwoFactorSubmit,
    handleForgotSubmit,
    handleResetSubmit,
  } = useLoginFlowHandlers({
    email,
    password,
    twoFactorCode,
    resetToken,
    newPassword,
    confirmNewPassword,
    honeypot,
    rememberMe,
    loading,
    rateLimit,
    loginIntentTime,
    executeRecaptcha: recaptcha.executeRecaptcha,
    recaptchaEnabled: recaptcha.enabled,
    login,
    persistRememberMe,
    redirectAfterAuth,
    setError,
    setLoading,
    setStep,
    setSuccessMessage,
    setResetToken,
    setNewPassword,
    setConfirmNewPassword,
    setPassword,
  })

  const goBack = () => {
    setError('')
    setSuccessMessage('')
    setStep('credentials')
    setTwoFactorCode('')
    setResetToken('')
    setNewPassword('')
    setConfirmNewPassword('')
  }

  const goToForgotPassword = () => {
    setPreferLocalLogin(true)
    setStep('forgotPassword')
    setError('')
    setSuccessMessage('')
  }

  const showLocalCredentials =
    !oidcStatus?.oidcEnabled || preferLocalLogin || step !== 'credentials'

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    rememberMe,
    setRememberMe,
    error,
    loading,
    step,
    setStep,
    twoFactorCode,
    setTwoFactorCode,
    resetToken,
    setResetToken,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    successMessage,
    honeypot,
    setHoneypot,
    rateLimit,
    handleLoginSubmit,
    handleTwoFactorSubmit,
    handleForgotSubmit,
    handleResetSubmit,
    goBack,
    goToForgotPassword,
    isAnyPending: loading || oidcRedirecting,
    oidcStatus,
    oidcRedirecting,
    showLocalCredentials,
    preferLocalLogin,
  }
}
